import React, { useReducer, useRef, useCallback, useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import useMouse from 'react-use/lib/useMouse';

import {
  ADD_APP,
  DEL_APP,
  FOCUS_APP,
  MINIMIZE_APP,
  TOGGLE_MAXIMIZE_APP,
  FOCUS_ICON,
  SELECT_ICONS,
  SET_ICONS,
  FOCUS_DESKTOP,
  START_SELECT,
  END_SELECT,
  POWER_OFF,
  CANCEL_POWER_OFF,
} from './constants/actions';
import { FOCUSING, POWER_STATE } from './constants';
import {
  defaultAppState,
  appSettings,
  createBuiltInDesktopIcons,
  createInternetExplorerApp,
  createEmbeddedPageApp,
} from './apps';
import Modal from './Modal';
import Footer from './Footer';
import Windows from './Windows';
import Icons from './Icons';
import BootScreen from './BootScreen';
import { DashedBox } from 'components';
import {
  fallbackSiteConfig,
  loadSiteConfig,
  toStartMenuItems,
  mergeDesktopIcons,
} from './siteConfig';

const BOOT_MEMORY_KEY = 'tongfu_xp_boot_completed_at';
const BOOT_SKIP_MS = 5 * 60 * 1000;
const initState = {
  apps: defaultAppState,
  nextAppID: defaultAppState.length,
  nextZIndex: defaultAppState.length,
  focusing: FOCUSING.WINDOW,
  icons: createBuiltInDesktopIcons(),
  selecting: false,
  powerState: POWER_STATE.START,
};

const reducer = (state, action = { type: '' }) => {
  switch (action.type) {
    case ADD_APP: {
      const app = state.apps.find(
        _app => _app.component === action.payload.component,
      );
      if (action.payload.multiInstance || !app) {
        return {
          ...state,
          apps: [
            ...state.apps,
            {
              ...action.payload,
              id: state.nextAppID,
              zIndex: state.nextZIndex,
            },
          ],
          nextAppID: state.nextAppID + 1,
          nextZIndex: state.nextZIndex + 1,
          focusing: FOCUSING.WINDOW,
        };
      }
      const apps = state.apps.map(existed =>
        existed.component === action.payload.component
          ? { ...existed, zIndex: state.nextZIndex, minimized: false }
          : existed,
      );
      return {
        ...state,
        apps,
        nextZIndex: state.nextZIndex + 1,
        focusing: FOCUSING.WINDOW,
      };
    }
    case DEL_APP:
      if (state.focusing !== FOCUSING.WINDOW) return state;
      return {
        ...state,
        apps: state.apps.filter(app => app.id !== action.payload),
        focusing:
          state.apps.length > 1
            ? FOCUSING.WINDOW
            : state.icons.find(icon => icon.isFocus)
            ? FOCUSING.ICON
            : FOCUSING.DESKTOP,
      };
    case FOCUS_APP: {
      const apps = state.apps.map(app =>
        app.id === action.payload
          ? { ...app, zIndex: state.nextZIndex, minimized: false }
          : app,
      );
      return {
        ...state,
        apps,
        nextZIndex: state.nextZIndex + 1,
        focusing: FOCUSING.WINDOW,
      };
    }
    case MINIMIZE_APP: {
      if (state.focusing !== FOCUSING.WINDOW) return state;
      const apps = state.apps.map(app =>
        app.id === action.payload ? { ...app, minimized: true } : app,
      );
      return {
        ...state,
        apps,
        focusing: FOCUSING.WINDOW,
      };
    }
    case TOGGLE_MAXIMIZE_APP: {
      if (state.focusing !== FOCUSING.WINDOW) return state;
      const apps = state.apps.map(app =>
        app.id === action.payload ? { ...app, maximized: !app.maximized } : app,
      );
      return {
        ...state,
        apps,
        focusing: FOCUSING.WINDOW,
      };
    }
    case FOCUS_ICON: {
      const icons = state.icons.map(icon => ({
        ...icon,
        isFocus: icon.id === action.payload,
      }));
      return {
        ...state,
        focusing: FOCUSING.ICON,
        icons,
      };
    }
    case SELECT_ICONS: {
      const icons = state.icons.map(icon => ({
        ...icon,
        isFocus: action.payload.includes(icon.id),
      }));
      return {
        ...state,
        icons,
        focusing: FOCUSING.ICON,
      };
    }
    case SET_ICONS:
      return {
        ...state,
        icons: action.payload,
        focusing: FOCUSING.DESKTOP,
      };
    case FOCUS_DESKTOP:
      return {
        ...state,
        focusing: FOCUSING.DESKTOP,
        icons: state.icons.map(icon => ({
          ...icon,
          isFocus: false,
        })),
      };
    case START_SELECT:
      return {
        ...state,
        focusing: FOCUSING.DESKTOP,
        icons: state.icons.map(icon => ({
          ...icon,
          isFocus: false,
        })),
        selecting: action.payload,
      };
    case END_SELECT:
      return {
        ...state,
        selecting: null,
      };
    case POWER_OFF:
      return {
        ...state,
        powerState: action.payload,
      };
    case CANCEL_POWER_OFF:
      return {
        ...state,
        powerState: POWER_STATE.START,
      };
    default:
      return state;
  }
};

function WinXP() {
  const [state, dispatch] = useReducer(reducer, initState);
  const [siteConfig, setSiteConfig] = useState(fallbackSiteConfig);
  const [desktopReady, setDesktopReady] = useState(() => {
    try {
      const lastCompletedAt = Number(localStorage.getItem(BOOT_MEMORY_KEY) || 0);
      return Date.now() - lastCompletedAt < BOOT_SKIP_MS;
    } catch (error) {
      return false;
    }
  });
  const [startMenuConfig, setStartMenuConfig] = useState({ left: [], right: [] });
  const ref = useRef(null);
  const appOpenCounterRef = useRef(0);
  const mouse = useMouse(ref);
  const focusedAppId = getFocusedAppId();

  useEffect(() => {
    const loadedConfig = loadSiteConfig();
    setSiteConfig(loadedConfig);
    setStartMenuConfig({
      left: toStartMenuItems(loadedConfig.startMenu.left),
      right: toStartMenuItems(loadedConfig.startMenu.right),
    });
    const builtInIcons = createBuiltInDesktopIcons();
    const mergedIcons = mergeDesktopIcons(loadedConfig.desktopIcons, builtInIcons);
    dispatch({ type: SET_ICONS, payload: mergedIcons });
  }, []);

  const onFocusApp = useCallback(id => {
    dispatch({ type: FOCUS_APP, payload: id });
  }, []);

  const onMaximizeWindow = useCallback(
    id => {
      if (focusedAppId === id) {
        dispatch({ type: TOGGLE_MAXIMIZE_APP, payload: id });
      }
    },
    [focusedAppId],
  );

  const onMinimizeWindow = useCallback(
    id => {
      if (focusedAppId === id) {
        dispatch({ type: MINIMIZE_APP, payload: id });
      }
    },
    [focusedAppId],
  );

  const onCloseApp = useCallback(
    id => {
      if (focusedAppId === id) {
        dispatch({ type: DEL_APP, payload: id });
      }
    },
    [focusedAppId],
  );

  const onIconsSelected = useCallback(iconIds => {
    dispatch({ type: SELECT_ICONS, payload: iconIds });
  }, []);

  const logoPath = useMemo(
    () => siteConfig.heroLogo || '/assets/branding/tongfu-inn-logo.png',
    [siteConfig],
  );

  function onMouseDownFooterApp(id) {
    if (focusedAppId === id) {
      dispatch({ type: MINIMIZE_APP, payload: id });
    } else {
      dispatch({ type: FOCUS_APP, payload: id });
    }
  }

  function onMouseDownIcon(id) {
    dispatch({ type: FOCUS_ICON, payload: id });
  }

  function isPasswordProtected(item) {
    if (!item) return false;
    const hasItemFlag =
      typeof item.passwordProtected === 'boolean' ||
      typeof item.password_protected === 'boolean';
    if (hasItemFlag) {
      return Boolean(item.passwordProtected ?? item.password_protected);
    }
    return Boolean(siteConfig.password_protected);
  }

  function buildProtectedUrl(item) {
    const key = `pw_gate_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    const payload = {
      target: item.url || '',
      hash: siteConfig.password_sha1 || '',
      title: item.text || item.title || item.name || '受保护内容',
      expiresAt: Date.now() + 15 * 60 * 1000,
    };
    try {
      localStorage.setItem(key, JSON.stringify(payload));
      return `/password-gate.html?k=${encodeURIComponent(key)}`;
    } catch (error) {
      return '/password-gate.html';
    }
  }

  function openByAction(item) {
    if (!item || !item.url) return;
    const action = item.action || 'redirect';
    const targetUrl = isPasswordProtected(item) ? buildProtectedUrl(item) : item.url;

    if (action === 'ie') {
      const appPayload = createInternetExplorerApp({
        url: targetUrl,
        instanceIndex: appOpenCounterRef.current++,
      });
      dispatch({ type: ADD_APP, payload: appPayload });
      return;
    }

    if (action === 'window') {
      const appPayload = createEmbeddedPageApp({
        title: item.text || item.title || '网页窗口',
        url: targetUrl,
        instanceIndex: appOpenCounterRef.current++,
      });
      dispatch({ type: ADD_APP, payload: appPayload });
      return;
    }

    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  }

  function onDoubleClickIcon(iconAction) {
    if (iconAction.actionType === 'config' && iconAction.url) {
      openByAction(iconAction);
      return;
    }

    const appSetting =
      (iconAction.appKey && appSettings[iconAction.appKey]) ||
      Object.values(appSettings).find(setting => setting.component === iconAction.component);

    if (appSetting) {
      dispatch({ type: ADD_APP, payload: appSetting });
    }
  }

  function getFocusedAppId() {
    if (state.focusing !== FOCUSING.WINDOW) return -1;
    const focusedApp = [...state.apps]
      .sort((a, b) => b.zIndex - a.zIndex)
      .find(app => !app.minimized);
    return focusedApp ? focusedApp.id : -1;
  }

  function onMouseDownFooter() {
    dispatch({ type: FOCUS_DESKTOP });
  }

  function onClickMenuItem(item) {
    openByAction(item);
  }

  function onPowerAction(action) {
    if (action === 'logoff') {
      dispatch({ type: POWER_OFF, payload: POWER_STATE.LOG_OFF });
    }
    if (action === 'poweroff') {
      dispatch({ type: POWER_OFF, payload: POWER_STATE.TURN_OFF });
    }
  }

  function onMouseDownDesktop(e) {
    if (e.target === e.currentTarget) {
      dispatch({
        type: START_SELECT,
        payload: { x: mouse.docX, y: mouse.docY },
      });
    }
  }

  function onMouseUpDesktop() {
    dispatch({ type: END_SELECT });
  }

  function onClickModalButton() {
    dispatch({ type: CANCEL_POWER_OFF });
    dispatch({
      type: ADD_APP,
      payload: {
        ...appSettings.Error,
        injectProps: { message: 'C:\\n当前演示中该按钮未启用。' },
      },
    });
  }

  function onModalClose() {
    dispatch({ type: CANCEL_POWER_OFF });
  }

  function onBootDone() {
    setDesktopReady(true);
    try {
      localStorage.setItem(BOOT_MEMORY_KEY, `${Date.now()}`);
    } catch (error) {
      // ignore storage errors
    }
  }

  return (
    <>
      {!desktopReady && (
        <BootScreen
          lines={siteConfig.boot.lines}
          typingSpeed={siteConfig.boot.typingSpeed}
          fadeDelayMs={siteConfig.boot.fadeDelayMs}
          onDone={onBootDone}
        />
      )}
      <Container
        ref={ref}
        onMouseUp={onMouseUpDesktop}
        onMouseDown={onMouseDownDesktop}
        state={state.powerState}
        className={desktopReady ? 'ready' : ''}
      >
        <Icons
          icons={state.icons}
          onMouseDown={onMouseDownIcon}
          onDoubleClick={onDoubleClickIcon}
          displayFocus={state.focusing === FOCUSING.ICON}
          mouse={mouse}
          selecting={state.selecting}
          setSelectedIcons={onIconsSelected}
        />
        <LogoBlock>
          <img src={logoPath} alt={siteConfig.guildName || '同福客栈'} />
        </LogoBlock>
        <DashedBox startPos={state.selecting} mouse={mouse} />
        <Windows
          apps={state.apps}
          onMouseDown={onFocusApp}
          onClose={onCloseApp}
          onMinimize={onMinimizeWindow}
          onMaximize={onMaximizeWindow}
          focusedAppId={focusedAppId}
        />
        <Footer
          apps={state.apps}
          onMouseDownApp={onMouseDownFooterApp}
          focusedAppId={focusedAppId}
          onMouseDown={onMouseDownFooter}
          onClickMenuItem={onClickMenuItem}
          onClickPowerAction={onPowerAction}
          guildName={siteConfig.guildName}
          startMenuConfig={startMenuConfig}
        />
        {state.powerState !== POWER_STATE.START && (
          <Modal
            onClose={onModalClose}
            onClickButton={onClickModalButton}
            mode={state.powerState}
          />
        )}
      </Container>
    </>
  );
}

const powerOffAnimation = keyframes`
  0% {
    filter: brightness(1) grayscale(0);
  }
  30% {
    filter: brightness(1) grayscale(0);
  }
  100% {
    filter: brightness(0.6) grayscale(1);
  }
`;

const animation = {
  [POWER_STATE.START]: '',
  [POWER_STATE.TURN_OFF]: powerOffAnimation,
  [POWER_STATE.LOG_OFF]: powerOffAnimation,
};

const fadeInDesktop = keyframes`
  from {
    opacity: 0;
    transform: scale(1.01);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const Container = styled.div`
  font-family: Tahoma, 'STHeiti', 'Microsoft YaHei', sans-serif;
  height: 100%;
  overflow: hidden;
  position: relative;
  background: url('/assets/wallpaper.png') no-repeat center center fixed;
  background-size: cover;
  animation: ${({ state }) => animation[state]} 5s forwards;
  opacity: 0;
  &.ready {
    opacity: 1;
    animation: ${fadeInDesktop} 900ms ease forwards,
      ${({ state }) => animation[state]} 5s forwards;
  }
  *:not(input):not(textarea) {
    user-select: none;
  }
`;

const LogoBlock = styled.section`
  position: absolute;
  right: 36px;
  top: 28px;
  pointer-events: none;
  img {
    display: block;
    width: min(42vw, 760px);
    height: auto;
    max-height: 32vh;
    object-fit: contain;
  }
`;

export default WinXP;
